import type {
  ICanCommitImportProps,
  IImportStageTransitionProps,
  IResolveImportTargetProps,
  ISkillCommitDecision,
} from "../types/skill-ui";
import {
  ESkillCommitBlockReason,
  ESkillImportSource,
  ESkillImportStage,
  ESkillImportTarget,
} from "../types/skill-ui";

// NOTE: NSAI-3307 — the import flow as a state machine, shared so web and mobile
// cannot disagree about when an import may be committed.
//
// The security-relevant property is the shape of the graph, not any single
// guard: COMMITTING is reachable ONLY from CONFIRMING, and CONFIRMING only from
// PREVIEWING. "Never import before a successful preview" is therefore something
// the transitions enforce, rather than a rule each screen has to remember.

const ALLOWED_TRANSITIONS: Readonly<
  Record<ESkillImportStage, readonly ESkillImportStage[]>
> = {
  [ESkillImportStage.INPUT]: [
    // NOTE: A paste goes straight to preview; a ZIP has to reach S3 first.
    ESkillImportStage.UPLOADING,
    ESkillImportStage.PREVIEWING,
  ],
  [ESkillImportStage.UPLOADING]: [
    ESkillImportStage.PREVIEWING,
    // NOTE: A cancelled or failed upload returns to the form, never forward.
    ESkillImportStage.INPUT,
    ESkillImportStage.FAILED,
  ],
  [ESkillImportStage.PREVIEWING]: [
    ESkillImportStage.CONFIRMING,
    ESkillImportStage.INPUT,
    ESkillImportStage.FAILED,
  ],
  [ESkillImportStage.CONFIRMING]: [
    ESkillImportStage.COMMITTING,
    // NOTE: Editing the input invalidates the preview and sends the flow back to
    // the start — a stale preview must never be committed.
    ESkillImportStage.INPUT,
  ],
  [ESkillImportStage.COMMITTING]: [
    ESkillImportStage.RESULT,
    ESkillImportStage.FAILED,
  ],
  // NOTE: A finished import is terminal for this attempt. Importing again starts
  // a new attempt from INPUT, which is what forces a fresh requestId.
  [ESkillImportStage.RESULT]: [ESkillImportStage.INPUT],
  [ESkillImportStage.FAILED]: [
    // NOTE: A failure may be retried from the preview it already had, or the
    // whole attempt restarted. It may NOT jump to COMMITTING.
    ESkillImportStage.INPUT,
    ESkillImportStage.PREVIEWING,
    ESkillImportStage.CONFIRMING,
  ],
};

export const canTransitionImportStage = ({
  from,
  to,
}: IImportStageTransitionProps): boolean =>
  ALLOWED_TRANSITIONS[from].includes(to);

// NOTE: Returns the next stage, or the current one when the move is not allowed.
// A rejected transition is a no-op rather than a throw: a double-tapped confirm
// button should do nothing, not crash the dialog.
export const nextImportStage = ({
  from,
  to,
}: IImportStageTransitionProps): ESkillImportStage =>
  canTransitionImportStage({ from, to }) ? to : from;

// NOTE: NSAI-3307 — the target uuid that may actually leave a client, derived
// from the selected target rather than read straight out of state.
//
// Every preview and commit request carries `targetSkillUuid` whenever it is
// non-null, and the SERVER uses that field alone to choose the update path. So a
// uuid left behind after someone picks "update an existing skill", selects one,
// then switches back to "create a new skill" would overwrite that skill while the
// dialog says it is creating one. Clearing the state on the switch is the first
// half of the fix; deriving it here is what makes a future caller unable to
// reintroduce the bug.
export const resolveImportTargetSkillUuid = ({
  target,
  targetSkillUuid,
}: IResolveImportTargetProps): string | null =>
  target === ESkillImportTarget.EXISTING ? targetSkillUuid : null;

// NOTE: A ZIP is the only source that uploads bytes before it can be previewed.
export const isUploadRequiredForSource = (
  source: ESkillImportSource,
): boolean => source === ESkillImportSource.ZIP;

// NOTE: The single answer to "may this import be committed?", returning WHY not
// so the dialog can explain itself instead of just disabling a button.
//
// The checks are ordered from most to least fundamental, so the reason a user
// sees is the first thing they have to fix.
export const resolveCommitDecision = ({
  stage,
  isPreviewValid,
  hasErrors,
  hasScripts,
  isScriptRiskAcknowledged,
  target,
  targetSkillUuid,
  hasRemovedFiles,
  isFileRemovalAcknowledged,
}: ICanCommitImportProps): ISkillCommitDecision => {
  if (stage !== ESkillImportStage.CONFIRMING) {
    return { canCommit: false, blockedBy: ESkillCommitBlockReason.WRONG_STAGE };
  }

  if (!isPreviewValid) {
    return {
      canCommit: false,
      blockedBy: ESkillCommitBlockReason.PREVIEW_INVALID,
    };
  }

  // NOTE: Checked independently of `isPreviewValid` rather than trusting it. A
  // preview reports parse problems as DATA, so a response could carry
  // `valid: true` alongside a populated manifest-error list; refusing on either
  // signal is the safe reading.
  if (hasErrors) {
    return { canCommit: false, blockedBy: ESkillCommitBlockReason.HAS_ERRORS };
  }

  // NOTE: Scripts require a SEPARATE, explicit acknowledgement — they cannot run
  // in NodeShift yet, but importing one publishes instructions nobody here has
  // reviewed to everyone the skill is visible to.
  if (hasScripts && !isScriptRiskAcknowledged) {
    return {
      canCommit: false,
      blockedBy: ESkillCommitBlockReason.SCRIPTS_NOT_ACKNOWLEDGED,
    };
  }

  // NOTE: Updating an existing skill without a resolved target would silently
  // create a second skill with the same name instead.
  if (target === ESkillImportTarget.EXISTING && !targetSkillUuid) {
    return {
      canCommit: false,
      blockedBy: ESkillCommitBlockReason.TARGET_MISSING,
    };
  }

  // NOTE: NSAI-5805 — an update is a FULL REPLACE: a file the bundle omits is
  // permanently deleted and there is no undo. Held to the same standard as the
  // script acknowledgement above and for the same reason — it destroys something
  // the person may not know they have, so it cannot be folded into the import
  // button. Checked last because the four above are reasons the import cannot
  // happen at all, while this one is a decision only worth asking for once the
  // import is otherwise ready.
  if (hasRemovedFiles && !isFileRemovalAcknowledged) {
    return {
      canCommit: false,
      blockedBy: ESkillCommitBlockReason.FILE_REMOVAL_NOT_ACKNOWLEDGED,
    };
  }

  return { canCommit: true, blockedBy: null };
};
